import React, { useState, useCallback, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  UniqueIdentifier,
  closestCenter,
  type CollisionDetection,
  type Collision,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { 
  PickGroupRankConfig, 
  PickGroupRankAnswer,
  getItemLabel 
} from "@/types/survey";
import { GripVertical, Check, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface PickGroupRankInputProps {
  config: PickGroupRankConfig;
  value: PickGroupRankAnswer | null;
  onChange: (answer: PickGroupRankAnswer) => void;
  disabled?: boolean;
  isMobile: boolean;
}

// ============================================
// Sortable Item Component
// ============================================

interface SortableItemProps {
  id: string;
  label: string;
  containerId: string;
  isRanked: boolean;
  rank?: number;
  isDragging?: boolean;
  isSelected?: boolean;
  onTapSelect?: () => void;
  disabled?: boolean;
}

function SortableItem({ 
  id, 
  label, 
  containerId,
  isRanked,
  rank,
  isDragging,
  isSelected,
  onTapSelect,
  disabled
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ 
    id,
    data: { containerId },
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isActive = isDragging || isSortableDragging;

  // Handle keyboard selection (Enter/Space for tap-to-select)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;
    
    // Enter or Space triggers tap-to-select
    if ((e.key === "Enter" || e.key === " ") && onTapSelect) {
      e.preventDefault();
      e.stopPropagation();
      onTapSelect();
    }
  }, [disabled, onTapSelect]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 p-3 bg-card rounded-md border border-border",
        "cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow",
        // Visible focus indicator for keyboard navigation
        "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1",
        isActive && "opacity-50 scale-105 shadow-md z-10",
        isSelected && "ring-2 ring-primary ring-offset-1",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      onClick={(e) => {
        // Tap-to-select for mobile/accessibility
        if (onTapSelect && !disabled) {
          e.stopPropagation();
          onTapSelect();
        }
      }}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      role="option"
      aria-selected={isSelected}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
      <span className="flex-1 text-sm">{label}</span>
      {isRanked && rank !== undefined && (
        <Badge variant="outline" className="shrink-0 font-mono text-xs">
          #{rank}
        </Badge>
      )}
    </div>
  );
}

// ============================================
// Droppable Container Component
// ============================================

interface DroppableContainerProps {
  id: string;
  label: string;
  items: string[];
  config: PickGroupRankConfig;
  isPool?: boolean;
  validationError?: string;
  hasSelectedItem?: boolean;
  isFocused?: boolean;
  onContainerSelect?: () => void;
  onContainerFocus?: () => void;
  children: React.ReactNode;
}

function DroppableContainer({ 
  id, 
  label, 
  items, 
  config: _config,
  isPool,
  validationError,
  hasSelectedItem,
  isFocused,
  onContainerSelect,
  onContainerFocus,
  children 
}: DroppableContainerProps) {
  const { setNodeRef, isOver } = useDroppable({ id });

  // Handle keyboard selection (Enter to place selected item)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && hasSelectedItem && onContainerSelect) {
      e.preventDefault();
      e.stopPropagation();
      onContainerSelect();
    }
  }, [hasSelectedItem, onContainerSelect]);

  return (
    <Card
      ref={setNodeRef}
      data-container-id={id}
      className={cn(
        "min-h-[200px] transition-all duration-200",
        isPool && "bg-muted/30",
        validationError && "border-destructive",
        isFocused && "ring-2 ring-primary ring-offset-1",
        hasSelectedItem && !isFocused && "focus-within:ring-2 focus-within:ring-primary"
      )}
      role="listbox"
      aria-label={`${label} - ${items.length} item${items.length !== 1 ? "s" : ""}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onFocus={onContainerFocus}
    >
      <CardHeader className={cn(
        "py-3 px-4",
        !isPool && "bg-muted/50",
        validationError && "bg-destructive/10"
      )}>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <span className="flex items-center gap-1">
            {label}
            {validationError && (
              <AlertCircle className="h-3.5 w-3.5 text-destructive ml-1" aria-hidden="true" />
            )}
          </span>
        </CardTitle>
      </CardHeader>
      
      {/* Validation Error */}
      {validationError && (
        <div className="px-4 py-1.5 bg-destructive/10 text-xs text-destructive" role="alert">
          {validationError}
        </div>
      )}
      
      <CardContent className="p-2">
        {/* Droppable Area */}
        <div
          className={cn(
            "min-h-[120px] rounded-lg border-2 border-dashed transition-colors p-2 space-y-2",
            isOver && "border-primary bg-primary/5",
            !isOver && "border-border"
          )}
        >
          {children}
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              {isPool ? "All items have been assigned" : "Drag items here"}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Drag Overlay Item Component
// ============================================

function DragOverlayItem({ label, isRanked: _isRanked }: { label: string; isRanked: boolean }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-card rounded-md border border-primary shadow-lg">
      <GripVertical className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

// ============================================
// Grid Column Helper (Static Tailwind classes)
// ============================================

function getGridColsClass(count: number, _isMobile: boolean): string {
  // Responsive grid per guide: grid-cols-1 md:grid-cols-2 lg:grid-cols-3
  // For fewer groups, cap the max columns
  switch (count) {
    case 1: return "grid-cols-1";
    case 2: return "grid-cols-1 md:grid-cols-2";
    default: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
  }
}

// ============================================
// Main Component
// ============================================

export function PickGroupRankInput({ 
  config, 
  value, 
  onChange,
  disabled = false,
  isMobile,
}: PickGroupRankInputProps) {
  // Local state for drag preview (ephemeral during drag)
  const [localAnswer, setLocalAnswer] = useState<PickGroupRankAnswer | null>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  
  // Announcements for screen readers
  const [announcement, setAnnouncement] = useState<string>("");
  
  // Track focused container for arrow key navigation
  const [focusedContainerId, setFocusedContainerId] = useState<string | null>(null);
  
  // Track whether this drag is pointer-based (vs keyboard)
  const [isPointerDrag, setIsPointerDrag] = useState(false);
  
  // Helper to get container label
  const getContainerLabel = useCallback((containerId: string): string => {
    if (containerId === "pool") return "Items to Sort";
    return config.groups.find(g => g.id === containerId)?.label || containerId;
  }, [config.groups]);
  
  // Get ordered list of container IDs for arrow navigation
  const containerIds = useMemo(() => {
    const ids: string[] = [];
    if (config.showItemPool) ids.push("pool");
    ids.push(...config.groups.map(g => g.id));
    return ids;
  }, [config.showItemPool, config.groups]);

  // Use local answer during drag, otherwise use prop value
  const currentAnswer = localAnswer ?? value ?? createInitialAnswer(config);

  // Sensors for drag detection (guide lines 1113-1122)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement to start drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,     // 200ms delay prevents accidental drags while scrolling
        tolerance: 5,   // Allow 5px movement during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get the set of valid container IDs (pool + group IDs)
  const containerIdSet = useMemo(() => {
    const ids = new Set<string>();
    ids.add("pool");
    config.groups.forEach(g => ids.add(g.id));
    return ids;
  }, [config.groups]);

  // Custom collision detection: pointer-based for mouse, closestCenter fallback for keyboard
  // This fixes the issue where items "jump" to the wrong container in horizontal layouts
  const pointerInsideOnly: CollisionDetection = useCallback((args) => {
    const { pointerCoordinates, collisionRect, droppableContainers, droppableRects } = args;

    // Only consider actual containers (pool and groups), not individual items
    const containerDroppables = droppableContainers.filter(
      container => containerIdSet.has(String(container.id))
    );

    // Use actual pointer coordinates if available, otherwise fall back to collision rect center
    const x = pointerCoordinates?.x ?? (collisionRect.left + collisionRect.width / 2);
    const y = pointerCoordinates?.y ?? (collisionRect.top + collisionRect.height / 2);

    const hits: Collision[] = [];

    for (const container of containerDroppables) {
      const rect = droppableRects.get(container.id);
      if (!rect) continue;

      const isInside =
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom;

      if (isInside) {
        hits.push({ id: container.id, data: { droppableContainer: container } });
      }
    }

    // If we're inside one or more containers, pick the smallest area one
    // (prevents large wrappers from stealing focus)
    if (hits.length > 0) {
      hits.sort((a, b) => {
        const ra = droppableRects.get(a.id);
        const rb = droppableRects.get(b.id);
        if (!ra || !rb) return 0;
        const areaA = (ra.right - ra.left) * (ra.bottom - ra.top);
        const areaB = (rb.right - rb.left) * (rb.bottom - rb.top);
        return areaA - areaB;
      });
      return [hits[0]];
    }

    // FALLBACK STRATEGY:
    // - Pointer drags (mouse/touch): return [] to prevent snapping/jumping
    // - Keyboard drags: use closestCenter so accessibility works
    if (isPointerDrag) {
      return [];
    }

    // Keyboard fallback: allow closestCenter among containers only
    return closestCenter({ ...args, droppableContainers: containerDroppables });
  }, [containerIdSet, isPointerDrag]);

  // Find which container an item is in
  const findContainer = useCallback((itemId: string): string | null => {
    // Check if it's in a group
    for (const [groupId, items] of Object.entries(currentAnswer.groups)) {
      if (items.includes(itemId)) {
        return groupId;
      }
    }
    // Check if it's in the unsorted pool
    if (currentAnswer.unsorted.includes(itemId)) {
      return "pool";
    }
    return null;
  }, [currentAnswer]);

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id);
    setSelectedItemId(null); // Clear tap selection when dragging
    // Initialize local state with current answer for preview
    setLocalAnswer(currentAnswer);
    
    // Detect if this is a pointer-based drag (mouse/touch) vs keyboard
    // This is used to decide collision detection fallback behavior
    const activator = event.activatorEvent as Event | undefined;
    const isPointer =
      activator instanceof PointerEvent ||
      activator instanceof MouseEvent ||
      (typeof TouchEvent !== "undefined" && activator instanceof TouchEvent);
    setIsPointerDrag(isPointer);
  }, [currentAnswer]);

  // Handle drag over (only reorder within same container - cross-container moves happen on drop)
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || !localAnswer) return;

    const activeItemId = String(active.id);
    const overId = String(over.id);

    // Skip if hovering over a container directly (not an item)
    if (overId === "pool" || config.groups.some(g => g.id === overId)) {
      return;
    }

    // Find containers for both items
    const activeContainer = findContainer(activeItemId);
    const overContainer = findContainer(overId);

    // Only handle reordering within the same container
    // Cross-container moves are handled in handleDragEnd for better UX
    if (!activeContainer || !overContainer || activeContainer !== overContainer) {
      return;
    }

    // Reorder within the same container
    setLocalAnswer((prev) => {
      if (!prev) return prev;

      if (activeContainer === "pool") {
        const oldIndex = prev.unsorted.indexOf(activeItemId);
        const newIndex = prev.unsorted.indexOf(overId);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
        
        const newUnsorted = [...prev.unsorted];
        newUnsorted.splice(oldIndex, 1);
        newUnsorted.splice(newIndex, 0, activeItemId);
        return { ...prev, unsorted: newUnsorted };
      } else {
        const items = prev.groups[activeContainer] || [];
        const oldIndex = items.indexOf(activeItemId);
        const newIndex = items.indexOf(overId);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
        
        const newItems = [...items];
        newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, activeItemId);
        return {
          ...prev,
          groups: { ...prev.groups, [activeContainer]: newItems },
        };
      }
    });
  }, [localAnswer, findContainer, config.groups]);

  // Handle drag end (commit changes)
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!localAnswer) {
      setActiveId(null);
      return;
    }

    const finalAnswer = { ...localAnswer };

    if (over) {
      const activeItemId = String(active.id);
      const overId = String(over.id);

      // Find current container of the active item
      const activeContainer = findContainer(activeItemId);
      
      // Determine destination container
      let overContainer: string | null = null;
      if (overId === "pool" || config.groups.some(g => g.id === overId)) {
        // Dropped directly on a container
        overContainer = overId;
      } else {
        // Dropped on an item - find its container
        for (const [groupId, items] of Object.entries(finalAnswer.groups)) {
          if (items.includes(overId)) {
            overContainer = groupId;
            break;
          }
        }
        if (!overContainer && finalAnswer.unsorted.includes(overId)) {
          overContainer = "pool";
        }
      }

      if (activeContainer && overContainer) {
        if (activeContainer === overContainer) {
          // Handle reordering within the same container
          if (activeContainer === "pool") {
            const oldIndex = finalAnswer.unsorted.indexOf(activeItemId);
            const newIndex = finalAnswer.unsorted.indexOf(overId);
            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
              finalAnswer.unsorted = arrayMove(finalAnswer.unsorted, oldIndex, newIndex);
            }
          } else {
            const items = finalAnswer.groups[activeContainer];
            const oldIndex = items.indexOf(activeItemId);
            const newIndex = items.indexOf(overId);
            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
              finalAnswer.groups = {
                ...finalAnswer.groups,
                [activeContainer]: arrayMove(items, oldIndex, newIndex),
              };
            }
          }
        } else {
          // Handle cross-container move
          // Remove from source
          if (activeContainer === "pool") {
            finalAnswer.unsorted = finalAnswer.unsorted.filter(id => id !== activeItemId);
          } else {
            finalAnswer.groups = {
              ...finalAnswer.groups,
              [activeContainer]: finalAnswer.groups[activeContainer].filter(id => id !== activeItemId),
            };
          }
          
          // Add to destination
          if (overContainer === "pool") {
            finalAnswer.unsorted = [...finalAnswer.unsorted, activeItemId];
          } else {
            const destItems = [...(finalAnswer.groups[overContainer] || [])];
            destItems.push(activeItemId);
            finalAnswer.groups = {
              ...finalAnswer.groups,
              [overContainer]: destItems,
            };
          }
        }
      }
    }

    // Commit to parent
    onChange(finalAnswer);
    
    // Announce the movement for screen readers
    if (over) {
      const activeItemId = String(active.id);
      const itemLabel = getItemLabel(config, activeItemId);
      const sourceContainer = findContainer(activeItemId);
      const destContainer = over.id === "pool" || config.groups.some(g => g.id === String(over.id))
        ? String(over.id)
        : findContainer(String(over.id));
      
      if (sourceContainer && destContainer && sourceContainer !== destContainer) {
        setAnnouncement(`${itemLabel} moved to ${getContainerLabel(destContainer)}`);
      } else if (destContainer) {
        setAnnouncement(`${itemLabel} reordered in ${getContainerLabel(destContainer)}`);
      }
    }
    
    // Clear local state
    setLocalAnswer(null);
    setActiveId(null);
    setIsPointerDrag(false);
  }, [localAnswer, findContainer, config, onChange, getContainerLabel]);

  // Handle drag cancel
  const handleDragCancel = useCallback(() => {
    setLocalAnswer(null);
    setActiveId(null);
    setIsPointerDrag(false);
  }, []);

  // Tap-to-select for mobile/accessibility
  const handleTapSelect = useCallback((itemId: string) => {
    if (disabled) return;
    
    if (selectedItemId === itemId) {
      // Deselect if already selected
      setSelectedItemId(null);
    } else if (selectedItemId) {
      // Move selected item to the container of the clicked item
      const targetContainer = findContainer(itemId);
      const sourceContainer = findContainer(selectedItemId);
      
      if (sourceContainer && targetContainer && sourceContainer !== targetContainer) {
        const newAnswer = { ...currentAnswer };
        
        // Remove from source
        if (sourceContainer === "pool") {
          newAnswer.unsorted = currentAnswer.unsorted.filter(id => id !== selectedItemId);
        } else {
          newAnswer.groups = {
            ...currentAnswer.groups,
            [sourceContainer]: currentAnswer.groups[sourceContainer].filter(id => id !== selectedItemId),
          };
        }
        
        // Add to destination
        if (targetContainer === "pool") {
          newAnswer.unsorted = [...newAnswer.unsorted, selectedItemId];
        } else {
          newAnswer.groups = {
            ...newAnswer.groups,
            [targetContainer]: [...(newAnswer.groups[targetContainer] || []), selectedItemId],
          };
        }
        
        onChange(newAnswer);
      }
      setSelectedItemId(null);
    } else {
      // Select the item
      setSelectedItemId(itemId);
    }
  }, [selectedItemId, findContainer, currentAnswer, onChange, disabled]);

  // Handle container click for tap-to-select
  const handleContainerClick = useCallback((containerId: string) => {
    if (!selectedItemId || disabled) return;
    
    const sourceContainer = findContainer(selectedItemId);
    if (!sourceContainer || sourceContainer === containerId) {
      setSelectedItemId(null);
      return;
    }
    
    const newAnswer = { ...currentAnswer };
    
    // Remove from source
    if (sourceContainer === "pool") {
      newAnswer.unsorted = currentAnswer.unsorted.filter(id => id !== selectedItemId);
    } else {
      newAnswer.groups = {
        ...currentAnswer.groups,
        [sourceContainer]: currentAnswer.groups[sourceContainer].filter(id => id !== selectedItemId),
      };
    }
    
    // Add to destination
    if (containerId === "pool") {
      newAnswer.unsorted = [...newAnswer.unsorted, selectedItemId];
    } else {
      newAnswer.groups = {
        ...newAnswer.groups,
        [containerId]: [...(newAnswer.groups[containerId] || []), selectedItemId],
      };
    }
    
    onChange(newAnswer);
    
    // Announce the movement for screen readers
    const itemLabel = getItemLabel(config, selectedItemId);
    setAnnouncement(`${itemLabel} moved to ${getContainerLabel(containerId)}`);
    
    setSelectedItemId(null);
  }, [selectedItemId, findContainer, currentAnswer, onChange, disabled, config, getContainerLabel]);

  // Handle Arrow key navigation between containers
  const handleArrowNavigation = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;
    
    const { key } = e;
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) return;
    
    // Only handle arrow keys when focus is on/inside a container element
    // This prevents swallowing arrow keys from other inputs (e.g., text fields)
    const target = e.target as HTMLElement | null;
    const isContainerFocused = !!target?.closest?.("[data-container-id]");
    if (!isContainerFocused) return;
    
    e.preventDefault();
    
    const currentIndex = focusedContainerId 
      ? containerIds.indexOf(focusedContainerId)
      : -1;
    
    let nextIndex: number;
    
    // Left/Up = previous container, Right/Down = next container (with wraparound)
    if (key === "ArrowLeft" || key === "ArrowUp") {
      nextIndex = currentIndex <= 0 ? containerIds.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex >= containerIds.length - 1 ? 0 : currentIndex + 1;
    }
    
    const nextContainerId = containerIds[nextIndex];
    setFocusedContainerId(nextContainerId);
    
    // Announce for screen readers
    setAnnouncement(`Focused on ${getContainerLabel(nextContainerId)}`);
    
    // Focus the container element
    const containerElement = document.querySelector(`[data-container-id="${nextContainerId}"]`);
    if (containerElement instanceof HTMLElement) {
      containerElement.focus();
    }
  }, [disabled, focusedContainerId, containerIds, getContainerLabel]);

  // Get active item info for overlay
  const activeItem = useMemo(() => {
    if (!activeId) return null;
    return config.items.find(item => item.id === String(activeId));
  }, [activeId, config.items]);

  // Calculate validation status for real-time feedback
  const validationStatus = useMemo(() => {
    const totalSorted = Object.values(currentAnswer.groups).flat().length;
    const totalItems = config.items.length;
    const groupErrors: Record<string, string> = {};
    const globalErrors: string[] = [];

    // Check per-group validation
    if (config.validation?.minPerGroup !== undefined || config.validation?.maxPerGroup !== undefined) {
      for (const group of config.groups) {
        const groupItemCount = currentAnswer.groups[group.id]?.length || 0;
        
        if (config.validation?.minPerGroup !== undefined && groupItemCount < config.validation.minPerGroup) {
          groupErrors[group.id] = `Needs at least ${config.validation.minPerGroup} item${config.validation.minPerGroup !== 1 ? "s" : ""}`;
        } else if (config.validation?.maxPerGroup !== undefined && groupItemCount > config.validation.maxPerGroup) {
          groupErrors[group.id] = `Maximum ${config.validation.maxPerGroup} item${config.validation.maxPerGroup !== 1 ? "s" : ""} allowed`;
        }
      }
    }

    // Check requireAllItemsSorted
    if (config.requireAllItemsSorted && currentAnswer.unsorted.length > 0) {
      globalErrors.push(`${currentAnswer.unsorted.length} item${currentAnswer.unsorted.length !== 1 ? "s" : ""} still need to be sorted`);
    }

    // Check minTotalSorted
    if (config.validation?.minTotalSorted !== undefined && totalSorted < config.validation.minTotalSorted) {
      globalErrors.push(`Sort at least ${config.validation.minTotalSorted} items (${totalSorted} sorted)`);
    }

    const isValid = Object.keys(groupErrors).length === 0 && globalErrors.length === 0;
    const progress = totalItems > 0 ? Math.round((totalSorted / totalItems) * 100) : 0;

    return {
      isValid,
      groupErrors,
      globalErrors,
      totalSorted,
      totalItems,
      progress,
    };
  }, [currentAnswer, config]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerInsideOnly}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {/* Screen reader announcements */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
      >
        {announcement}
      </div>
      
      <div className="space-y-4" onKeyDown={handleArrowNavigation}>
        {/* Keyboard navigation hint (screen readers only) */}
        <div className="sr-only">
          Use arrow keys to navigate between containers. Press Enter or Space to select an item, then navigate to a container and press Enter to place it.
        </div>
        
        {/* Tap-to-select instruction */}
        {selectedItemId && (
          <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/30 rounded-lg text-sm text-foreground">
            <Check className="h-4 w-4 text-primary" aria-hidden="true" />
            <span>Tap a group to move the selected item, or tap another item to select it instead</span>
          </div>
        )}

        {/* Item Pool */}
        {config.showItemPool && (
          <div onClick={() => handleContainerClick("pool")}>
            <SortableContext 
              items={currentAnswer.unsorted} 
              strategy={verticalListSortingStrategy}
            >
              <DroppableContainer
                id="pool"
                label="Items to Sort"
                items={currentAnswer.unsorted}
                config={config}
                isPool
                hasSelectedItem={!!selectedItemId}
                isFocused={focusedContainerId === "pool"}
                onContainerSelect={() => handleContainerClick("pool")}
                onContainerFocus={() => setFocusedContainerId("pool")}
              >
                {currentAnswer.unsorted.map((itemId) => (
                  <SortableItem
                    key={itemId}
                    id={itemId}
                    label={getItemLabel(config, itemId)}
                    containerId="pool"
                    isRanked={false}
                    isDragging={activeId === itemId}
                    isSelected={selectedItemId === itemId}
                    onTapSelect={() => handleTapSelect(itemId)}
                    disabled={disabled}
                  />
                ))}
              </DroppableContainer>
            </SortableContext>
          </div>
        )}

        {/* Groups */}
        <div className={cn(
          "grid gap-4",
          config.displayMode === "columns" 
            ? getGridColsClass(config.groups.length, isMobile)
            : "grid-cols-1"
        )}>
          {config.groups.map((group) => {
            const groupItems = currentAnswer.groups[group.id] || [];
            return (
              <div 
                key={group.id} 
                onClick={() => handleContainerClick(group.id)}
              >
                <SortableContext 
                  items={groupItems} 
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableContainer
                    id={group.id}
                    label={group.label}
                    items={groupItems}
                    config={config}
                    validationError={validationStatus.groupErrors[group.id]}
                    hasSelectedItem={!!selectedItemId}
                    isFocused={focusedContainerId === group.id}
                    onContainerSelect={() => handleContainerClick(group.id)}
                    onContainerFocus={() => setFocusedContainerId(group.id)}
                  >
                    {groupItems.map((itemId, index) => (
                      <SortableItem
                        key={itemId}
                        id={itemId}
                        label={getItemLabel(config, itemId)}
                        containerId={group.id}
                        isRanked={config.allowRankingWithinGroups}
                        rank={config.allowRankingWithinGroups ? index + 1 : undefined}
                        isDragging={activeId === itemId}
                        isSelected={selectedItemId === itemId}
                        onTapSelect={() => handleTapSelect(itemId)}
                        disabled={disabled}
                      />
                    ))}
                  </DroppableContainer>
                </SortableContext>
              </div>
            );
          })}
        </div>

        {/* Validation Progress */}
        {(config.requireAllItemsSorted || config.validation?.minTotalSorted) && (
          <div className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg text-sm",
            validationStatus.isValid 
              ? "bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400"
              : "bg-muted border border-border text-muted-foreground"
          )}>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">
                  {validationStatus.isValid ? "✓ Requirements met" : "Progress"}
                </span>
                <span className="text-xs">
                  {validationStatus.totalSorted}/{validationStatus.totalItems} sorted
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5">
                <div 
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300",
                    validationStatus.isValid ? "bg-green-500" : "bg-amber-500"
                  )}
                  style={{ width: `${validationStatus.progress}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Global Validation Errors */}
        {validationStatus.globalErrors.length > 0 && (
          <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              {validationStatus.globalErrors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Drag Overlay - centered on cursor */}
      <DragOverlay modifiers={[snapCenterToCursor]}>
        {activeItem && (
          <DragOverlayItem 
            label={activeItem.label} 
            isRanked={config.allowRankingWithinGroups}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}

// ============================================
// Helper Functions
// ============================================

function createInitialAnswer(config: PickGroupRankConfig): PickGroupRankAnswer {
  // Initialize with all items in the unsorted pool
  const groups: Record<string, string[]> = {};
  for (const group of config.groups) {
    groups[group.id] = [];
  }
  
  return {
    groups,
    unsorted: config.items.map(item => item.id),
  };
}

export default PickGroupRankInput;
